import * as React from 'react';
import { Translate, I18n } from 'react-redux-i18n';

import InnerPanel from '../InnerPanel';
import ButtonRemove from '../../ButtonRemove';
import { InputWrapper, InputError } from '../../styles';
import { Label, Icon, Hint } from './styles';
import Utils from '../../../../../utils';
import { Api } from '../../../../../typings/api';

interface FieldPanelProps {
	name: Api.PublicationSettingName;
	value: string;
	label: string;
	icon: string;
	serverError: string | null;
	onConfirm: (name: Api.PublicationSettingName, value: string) => void;
	onClose: () => void;
	resetServerError: (name: Api.PublicationSettingName) => void;
	placeholder?: string;
	hint?: string;
	checkIsValid?: (value: string) => boolean;
	getMaskedValue?: (value: string) => string;
	renderChildren?: (value: string) => void;
}

const FieldPanel = ({
	name,
	value: initialValue,
	label,
	icon,
	placeholder = '',
	hint,
	serverError,
	renderChildren,
	checkIsValid,
	getMaskedValue,
	onConfirm,
	onClose,
	resetServerError,
}: FieldPanelProps) => {
	const [value, setValue] = React.useState(initialValue);
	const [isError, setIsError] = React.useState(false);
	const inputElement = React.useRef<HTMLInputElement>(null);

	const resetErrors = () => {
		if (isError) {
			setIsError(false);
		}

		if (serverError) {
			resetServerError(name);
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		resetErrors();
	};

	const handleClear = () => {
		setValue('');
		resetErrors();

		if (inputElement.current) {
			inputElement.current.focus();
		}
	};

	const handleConfirm = () => {
		if (value === initialValue) {
			return onClose();
		}

		const isValid = checkIsValid && value ? checkIsValid(value) : true;
		setIsError(!isValid);

		if (isValid) {
			const valueToSave = getMaskedValue ? getMaskedValue(value) : value;
			onConfirm(name, valueToSave);
		}
	};

	React.useEffect(() => {
		if (serverError) {
			setIsError(true);
		}

		return () => {
			if (serverError) {
				resetServerError(name);
			}
		};
	}, [serverError]);

	React.useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	return (
		<InnerPanel
			onConfirm={handleConfirm}
			onClose={onClose}
			isError={isError}
		>
			<Label htmlFor={name}>
				<Translate value={label} />
			</Label>
			<InputWrapper
				hasButtonRemove={true}
				isError={isError}
			>
				{serverError && <InputError value={serverError} />}
				<Icon>
					{Utils.getSvgIcon(icon)}
				</Icon>
				<input
					ref={inputElement}
					type="text"
					id={name}
					value={value}
					placeholder={I18n.t(placeholder)}
					onChange={handleChange}
					autoFocus={true}
				/>
				{value && <ButtonRemove onClick={handleClear} />}
			</InputWrapper>
			<Hint value={hint} />
			{renderChildren && renderChildren(value)}
		</InnerPanel>
	);
};

export default FieldPanel;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/SettingsPanel/FieldPanel/index.tsx